import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { MAX_SIZE, UPLOAD_FAILED, validatePhoto, tooManyPhotos } from "../modules/photo_upload.mjs"
import { purgeUpload } from "../modules/direct_upload_purge.js"

// Varias fotos por direct upload con tope (promocionales): una tarjeta por imagen con su hidden
// `name[]` y Quitar; publica `uploading` | `idle` en el value y en photo-list:change.
// Doc: _docs/features/images/photo_field.md
export default class extends Controller {
  static targets = ["input", "grid", "empty", "item", "template", "error"]
  static values = {
    uploadUrl: String,
    max: { type: Number, default: 2 },
    maxSize: { type: Number, default: MAX_SIZE },
    accept: Array, // content types que el modelo guarda (ImageCompressor::ACCEPTED_CONTENT_TYPES); vacío = cualquier imagen
    state: { type: String, default: "idle" }
  }

  // Un snapshot de Turbo (Atrás) trae tarjetas que seguían subiendo: ninguna subida las completa, se descartan
  connect() {
    this.itemTargets.filter(item => item.dataset.uploading).forEach(item => item.remove())
    this.#sync()
  }

  pick() {
    this.inputTarget.click()
  }

  // Agrega los archivos hasta completar el tope; los que sobran se avisan
  changed() {
    const files = [...(this.inputTarget.files || [])]
    this.inputTarget.value = ""
    if (!files.length) return
    this.#showError("")
    const room = Math.max(this.maxValue - this.itemTargets.length, 0)
    if (files.length > room) this.#showError(tooManyPhotos(this.maxValue))
    files.slice(0, room).forEach(file => {
      const problem = validatePhoto(file, this.maxSizeValue, this.acceptValue)
      problem ? this.#showError(problem) : this.#add(file)
    })
    this.#sync()
  }

  // Quitar: una subida de la sesión se purga; una guardada solo deja de viajar en el form
  remove(event) {
    const item = event.target.closest("[data-photo-list-target='item']")
    if (!item) return
    item.remove()
    if (item.dataset.sessionUpload) purgeUpload(item.dataset.sessionUpload)
    this.#sync()
  }

  stateValueChanged() {
    this.dispatch("change", { detail: { state: this.stateValue } })
  }

  #add(file) {
    const item = this.templateTarget.content.firstElementChild.cloneNode(true)
    const hidden = item.querySelector("input[type='hidden']")
    const progress = item.querySelector("[data-role='progress']")
    item.querySelector("img").src = URL.createObjectURL(file)
    item.dataset.uploading = "true"
    progress.hidden = false
    progress.textContent = "Subiendo… 0%"
    this.gridTarget.insertBefore(item, this.emptyTarget)
    const upload = new DirectUpload(file, this.uploadUrlValue, {
      directUploadWillStoreFileWithXHR: request => {
        request.upload.addEventListener("progress", event => {
          if (event.lengthComputable) progress.textContent = `Subiendo… ${Math.round((event.loaded / event.total) * 100)}%`
        })
      }
    })
    upload.create((error, blob) => {
      // Quitada antes de terminar (o la página cambió): su blob nunca entró al form, se purga
      if (!item.isConnected) return blob && purgeUpload(blob.signed_id)
      delete item.dataset.uploading
      if (error) {
        item.remove()
        this.#showError(UPLOAD_FAILED)
      } else {
        hidden.value = blob.signed_id
        item.dataset.sessionUpload = blob.signed_id
        progress.hidden = true
      }
      this.#sync()
    })
  }

  // La caja "Agregar foto" solo mientras quede cupo; el estado refleja si alguna sigue subiendo
  #sync() {
    const items = this.itemTargets
    this.emptyTarget.hidden = items.length >= this.maxValue
    this.stateValue = items.some(item => item.dataset.uploading) ? "uploading" : "idle"
  }

  #showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = !message
  }
}
